import { Link } from "react-router-dom";
import { useEffect } from "react";
import {
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  Crown,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/atoms";
import { AppLogo } from "@/components/atoms/AppLogo";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { LandingHeroScreenshot } from "@/components/landing/LandingHeroScreenshot";
import { LandingBenefitPreview } from "@/components/landing/LandingBenefitPreview";
import {
  LANDING_AUDIENCE,
  LANDING_BENEFITS,
  LANDING_EXAMPLES,
  LANDING_FAQS,
  LANDING_PRICING,
  LANDING_SPREADSHEET_ROWS,
  LANDING_STEPS,
  LANDING_VALUE_PROPS,
} from "@/content/landing-content";
import { LANDING_SEO } from "@/constants/site-seo";
import { applyLandingJsonLd, applyPageMeta } from "@/utils/page-meta.utils";
import { trackLandingCtaClick } from "@/services/firebase/analytics.service";

function SectionKicker({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[11px] font-black uppercase tracking-[0.18em] text-brand">
      {children}
    </p>
  );
}

function PrimaryCta({
  className = "",
  ctaId = "start_free",
}: {
  className?: string;
  ctaId?: string;
}) {
  return (
    <Link
      to="/signup"
      className={className}
      onClick={() => {
        void trackLandingCtaClick(ctaId);
      }}
    >
      <Button variant="primary" size="lg" className="min-w-[200px]">
        Start free
        <ArrowRight className="ml-2 h-4 w-4" />
      </Button>
    </Link>
  );
}

function SpreadsheetComparisonSection() {
  return (
    <section className="border-b border-white/[0.08] bg-surface-elevated/30">
      <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
          <div>
            <SectionKicker>Why not a spreadsheet?</SectionKicker>
            <h2 className="mt-2 text-3xl font-black text-white">
              Spreadsheets break the moment you share them
            </h2>
            <p className="mt-4 text-white/65">
              You end up with three versions, stale runway numbers, and deck figures
              that don&apos;t match your model. BurnChart keeps one live burn chart for
              you, your co-founder, and investors — change a hire date and every number
              updates instantly.
            </p>
          </div>
          <div className="sh-card divide-y divide-white/10 overflow-hidden">
            <div className="grid grid-cols-[1fr_auto_auto] items-center gap-4 bg-white/[0.03] px-5 py-3 text-xs font-bold uppercase tracking-wide text-white/45">
              <span>Capability</span>
              <span className="w-20 text-center">BurnChart</span>
              <span className="w-20 text-center">Spreadsheet</span>
            </div>
            {LANDING_SPREADSHEET_ROWS.map((label) => (
              <div
                key={label}
                className="grid grid-cols-[1fr_auto_auto] items-center gap-4 px-5 py-4 text-sm"
              >
                <span className="text-white/80">{label}</span>
                <span className="w-20 text-center font-semibold text-brand">✓</span>
                <span className="w-20 text-center text-white/35">—</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function LandingPage() {
  useEffect(() => {
    applyPageMeta({
      title: LANDING_SEO.title,
      description: LANDING_SEO.description,
      keywords: LANDING_SEO.keywords,
      canonicalPath: "/",
    });
    applyLandingJsonLd([...LANDING_FAQS]);
  }, []);

  return (
    <div className="min-h-screen bg-page text-white">
      <header className="sticky top-0 z-40 border-b border-white/[0.08] bg-page/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-4 sm:px-6 lg:px-8">
          <AppLogo to="/" size={36} variant="large" />
          <nav className="hidden items-center gap-6 text-sm font-semibold text-white/60 md:flex">
            <a href="#how-it-works" className="transition-colors hover:text-white">
              How it works
            </a>
            <a href="#features" className="transition-colors hover:text-white">
              Product
            </a>
            <a href="#pricing" className="transition-colors hover:text-white">
              Pricing
            </a>
            <a href="#examples" className="transition-colors hover:text-white">
              Examples
            </a>
            <Link to="/help" className="transition-colors hover:text-white">
              Help
            </Link>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/signin">
              <Button variant="ghost" size="sm">
                Sign in
              </Button>
            </Link>
            <Link to="/signup">
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  void trackLandingCtaClick("header_get_started");
                }}
              >
                Get started
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <section className="relative isolate overflow-hidden border-b border-white/[0.08]">
        <div className="pointer-events-none absolute inset-0 opacity-70" aria-hidden>
          <div className="absolute -top-32 left-1/2 h-[28rem] w-[28rem] -translate-x-1/2 rounded-full bg-brand/20 blur-[120px]" />
          <div className="absolute right-[-10%] top-20 h-[18rem] w-[18rem] rounded-full bg-focus/15 blur-[100px]" />
        </div>

        <div className="relative mx-auto max-w-6xl px-4 pb-16 pt-14 sm:px-6 lg:px-8 lg:pb-20 lg:pt-20">
          <div className="grid items-center gap-12 lg:grid-cols-[1.05fr_0.95fr]">
            <div>
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/15 bg-surface/[0.04] px-3 py-1 text-[11px] font-black uppercase tracking-[0.16em] text-white/80">
                <Sparkles className="h-3.5 w-3.5 text-brand" />
                For founders tired of guessing runway
              </div>
              <h1 className="text-balance text-4xl font-black tracking-tight sm:text-5xl lg:text-[3.25rem] lg:leading-[1.05]">
                Startup runway calculator &amp; burn chart{" "}
                <span className="bg-gradient-to-r from-brand via-white to-focus bg-clip-text text-transparent">
                  built for founders
                </span>
              </h1>
              <p className="mt-5 max-w-xl text-pretty text-lg leading-relaxed text-white/75">
                You&apos;re burning cash, your spreadsheet has three versions, and
                investors ask questions you can&apos;t answer confidently. BurnChart
                gives you one live burn chart — runway, revenue, funding, and
                shareable output on a single timeline.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                <PrimaryCta />
                <Link to="/help">
                  <Button variant="secondary" size="lg" className="min-w-[200px] w-full sm:w-auto">
                    See how it works
                  </Button>
                </Link>
              </div>
              <p className="mt-4 text-sm text-white/45">
                Free to start · No credit card · Setup in under an hour
              </p>
            </div>

            <LandingHeroScreenshot className="lg:translate-x-1 lg:shadow-[0_24px_80px_-12px_rgba(0,0,0,0.55)]" />
          </div>
        </div>
      </section>

      <section className="border-b border-white/[0.08] bg-surface-elevated/40">
        <div className="mx-auto grid max-w-6xl gap-6 px-4 py-10 sm:grid-cols-2 lg:grid-cols-4 sm:px-6 lg:px-8">
          {LANDING_VALUE_PROPS.map((prop) => (
            <div key={prop.label}>
              <p className="font-bold text-white">{prop.label}</p>
              <p className="mt-1 text-sm text-white/55">{prop.detail}</p>
            </div>
          ))}
        </div>
      </section>

      <SpreadsheetComparisonSection />

      <section id="how-it-works" className="mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <SectionKicker>How it works</SectionKicker>
          <h2 className="mt-2 text-3xl font-black text-white sm:text-4xl">
            From blank slate to investor-ready in four steps
          </h2>
          <p className="mt-3 text-white/65">
            BurnChart mirrors how founders actually think about cash — timeline first,
            then growth, then the ask.
          </p>
        </div>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {LANDING_STEPS.map((item) => (
            <div key={item.step} className="sh-card relative p-6">
              <span className="text-3xl font-black text-brand/30">{item.step}</span>
              <h3 className="mt-3 text-lg font-bold text-white">{item.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-white/60">{item.body}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 flex justify-center">
          <PrimaryCta />
        </div>
      </section>

      <section className="border-y border-white/[0.08] bg-surface-elevated/30">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="text-center">
            <SectionKicker>Built for</SectionKicker>
            <h2 className="mt-2 text-2xl font-black text-white sm:text-3xl">
              Founders who need numbers they can defend
            </h2>
          </div>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {LANDING_AUDIENCE.map(({ title, body, icon: Icon }) => (
              <div key={title} className="sh-panel p-6 text-center">
                <div className="mx-auto mb-4 inline-flex rounded-[14px] bg-brand/10 p-3 text-brand">
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="font-bold text-white">{title}</h3>
                <p className="mt-2 text-sm text-white/60">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="features" className="mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <SectionKicker>Product</SectionKicker>
          <h2 className="mt-2 text-3xl font-black text-white sm:text-4xl">
            One model for the questions that keep you up at night
          </h2>
          <p className="mt-3 text-white/65">
            Not a feature checklist — three outcomes founders actually need from their
            burn chart.
          </p>
        </div>
        <div className="mt-16 space-y-24">
          {LANDING_BENEFITS.map((benefit, index) => {
            const imageFirst = index % 2 === 1;
            return (
              <div
                key={benefit.title}
                className={`grid items-center gap-10 lg:grid-cols-2 ${
                  imageFirst ? "lg:[&>*:first-child]:order-2" : ""
                }`}
              >
                <div>
                  <SectionKicker>{benefit.kicker}</SectionKicker>
                  <h3 className="mt-2 text-2xl font-black text-white sm:text-3xl">
                    {benefit.title}
                  </h3>
                  <p className="mt-4 text-white/65">{benefit.body}</p>
                  <ul className="mt-6 space-y-2.5">
                    {benefit.bullets.map((bullet) => (
                      <li key={bullet} className="flex gap-2 text-sm text-white/75">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                        {bullet}
                      </li>
                    ))}
                  </ul>
                </div>
                <LandingBenefitPreview variant={benefit.preview} />
              </div>
            );
          })}
        </div>
      </section>

      <section id="examples" className="border-t border-white/[0.08] bg-surface-elevated/20">
        <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <SectionKicker>Examples</SectionKicker>
            <h2 className="mt-2 text-3xl font-black text-white sm:text-4xl">
              See yourself in the model
            </h2>
            <p className="mt-3 text-white/65">
              Three common founder paths — each maps cleanly to BurnChart inputs. Start
              with the closest match and adapt.
            </p>
          </div>
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {LANDING_EXAMPLES.map((example) => (
              <article
                key={example.title}
                className={`sh-card-elevated flex flex-col overflow-hidden bg-gradient-to-br ${example.accent}`}
              >
                <div className="flex flex-1 flex-col p-6">
                  <p className="text-xs font-semibold uppercase tracking-wide text-brand">
                    {example.subtitle}
                  </p>
                  <h3 className="mt-2 text-xl font-black text-white">{example.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/70">
                    {example.scenario}
                  </p>
                  <ul className="mt-5 space-y-2.5">
                    {example.bullets.map((bullet) => (
                      <li key={bullet} className="flex gap-2 text-sm text-white/75">
                        <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-5 border-t border-white/10 pt-4 text-sm font-semibold text-white/85">
                    {example.outcome}
                  </p>
                </div>
              </article>
            ))}
          </div>
          <div className="mt-12 rounded-[20px] border border-brand/25 bg-brand/10 p-8 text-center">
            <h3 className="text-xl font-black text-white">
              Your scenario is probably here
            </h3>
            <p className="mx-auto mt-2 max-w-xl text-sm text-white/70">
              Sign up, name your startup, and follow the in-app setup guide. Most
              founders have a credible first model in one sitting.
            </p>
            <div className="mt-6">
              <PrimaryCta className="inline-block" />
            </div>
          </div>
        </div>
      </section>

      <section id="pricing" className="border-t border-white/[0.08] bg-surface-elevated/25">
        <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <SectionKicker>Pricing</SectionKicker>
            <h2 className="mt-2 text-3xl font-black text-white sm:text-4xl">
              Free burn chart to start. Pro when you scale.
            </h2>
            <p className="mt-3 text-white/65">
              Model your first startup at no cost — upgrade when you need multiple
              companies, AI advisor, business plan generation, or API access.
            </p>
          </div>
          <div className="mt-12 grid gap-6 lg:grid-cols-2">
            <div className="sh-card flex flex-col p-8">
              <p className="text-sm font-bold uppercase tracking-wide text-white/50">
                {LANDING_PRICING.free.name}
              </p>
              <p className="mt-3 text-4xl font-black text-white">
                {LANDING_PRICING.free.price}
                <span className="ml-2 text-base font-semibold text-white/45">
                  {LANDING_PRICING.free.period}
                </span>
              </p>
              <p className="mt-3 text-sm text-white/65">{LANDING_PRICING.free.summary}</p>
              <ul className="mt-6 flex-1 space-y-2.5">
                {LANDING_PRICING.free.bullets.map((bullet) => (
                  <li key={bullet} className="flex gap-2 text-sm text-white/75">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                    {bullet}
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                <PrimaryCta className="inline-block" ctaId="pricing_free" />
              </div>
            </div>

            <div className="sh-card-elevated relative flex flex-col overflow-hidden border border-brand/30 bg-gradient-to-br from-brand/10 via-surface to-focus/5 p-8">
              <div className="absolute right-4 top-4 inline-flex items-center gap-1 rounded-full bg-brand px-2.5 py-1 text-[10px] font-black uppercase tracking-wide text-surface">
                <Crown className="h-3 w-3" />
                Pro
              </div>
              <p className="text-sm font-bold uppercase tracking-wide text-brand">
                {LANDING_PRICING.pro.name}
              </p>
              <p className="mt-3 text-4xl font-black text-white">
                ${LANDING_PRICING.pro.monthlyUsd}
                <span className="text-base font-semibold text-white/45">/mo</span>
              </p>
              <p className="mt-1 text-sm text-white/55">
                or ${LANDING_PRICING.pro.yearlyUsd}/year (save ~17%)
              </p>
              <p className="mt-3 text-sm text-white/65">{LANDING_PRICING.pro.summary}</p>
              <ul className="mt-6 flex-1 space-y-2.5">
                {LANDING_PRICING.pro.bullets.map((bullet) => (
                  <li key={bullet} className="flex gap-2 text-sm text-white/75">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                    {bullet}
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                <Link
                  to="/signup"
                  onClick={() => {
                    void trackLandingCtaClick("pricing_pro");
                  }}
                >
                  <Button variant="primary" size="lg" className="min-w-[200px]">
                    Start free, upgrade later
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-white/[0.08] bg-surface-elevated/30">
        <div className="mx-auto max-w-3xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="text-center">
            <SectionKicker>FAQ</SectionKicker>
            <h2 className="mt-2 text-3xl font-black text-white">Common questions</h2>
          </div>
          <dl className="mt-10 space-y-4">
            {LANDING_FAQS.map((faq) => (
              <div key={faq.question} className="sh-panel p-5">
                <dt className="font-bold text-white">{faq.question}</dt>
                <dd className="mt-2 text-sm leading-relaxed text-white/65">{faq.answer}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section className="border-t border-white/[0.08]">
        <div className="mx-auto max-w-3xl px-4 py-20 text-center sm:px-6">
          <AppLogo size={48} showWordmark={false} className="mx-auto justify-center" />
          <h2 className="mt-6 text-3xl font-black text-white sm:text-4xl">
            Ready to model your runway?
          </h2>
          <p className="mt-4 text-lg text-white/65">
            Create your account, add your startup, and see your burn chart in minutes.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <PrimaryCta />
            <Link to="/signin">
              <Button variant="secondary" size="lg" className="min-w-[200px]">
                I already have an account
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
